import {
  createTheme,
  responsiveFontSizes,
  type Theme,
  type ThemeOptions,
} from '@mui/material/styles';

// Accessibility-compliant color palette
// All color combinations meet WCAG 2.1 AA contrast requirements (4.5:1 for normal text, 3:1 for large text)
const palette = {
  primary: {
    main: '#1565C0', // Blue 800 - good contrast on white
    light: '#1976D2',
    dark: '#0D47A1',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#7B1FA2', // Purple 700 - good contrast on white
    light: '#9C27B0',
    dark: '#6A1B9A',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#C62828', // Red 800 - good contrast
    light: '#E53935',
    dark: '#B71C1C',
    contrastText: '#FFFFFF',
  },
  warning: {
    // #E65100 (Orange 900) is only 3.63:1 on the #FAFAFA app background — it
    // fails WCAG 2.2 AA (4.5:1) for normal-size text such as outlined/text
    // button labels ("Won't fix"). #BF360C (Deep Orange 900) is 5.46:1 and
    // keeps a clearly-orange "warning" read. White remains AA on it (5.46:1)
    // for contained buttons. Caught by the afixt-engine dogfood gate (CSS-01).
    main: '#BF360C',
    light: '#EF6C00', // hover/fill tint only — never used for normal-size text
    dark: '#8F2A00',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#2E7D32', // Green 800 - accessible
    light: '#388E3C',
    dark: '#1B5E20',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#0277BD', // Light Blue 800
    light: '#0288D1',
    dark: '#01579B',
    contrastText: '#FFFFFF',
  },
  text: {
    primary: '#212121', // High contrast for readability
    secondary: '#424242',
    disabled: '#757575',
  },
  background: {
    default: '#FAFAFA',
    paper: '#FFFFFF',
  },
  divider: '#E0E0E0',
};

/**
 * REV-491: dark palette.
 *
 * Every value below was computed against both dark surfaces (#121212 default,
 * #1E1E1E paper) rather than eyeballed, because this is an accessibility
 * product and dark mode has already been shipped here once as a dead toggle and
 * deleted for it (REV-445). Measured ratios, worst of the two surfaces:
 *
 *   primary #64B5F6   7.53:1   secondary #CE93D8  6.98:1
 *   error   #EF9A9A   7.75:1   warning   #FFB74D  9.63:1
 *   success #81C784   8.28:1   info      #4FC3F7  8.32:1
 *   text.primary #FFFFFF 16.67:1   text.secondary #C9C9C9 10.07:1
 *   text.disabled #8F8F8F 5.15:1   divider #6E6E6E 3.27:1 (non-text, needs 3:1)
 *
 * contrastText is BLACK on every accent. This is the trap: inheriting the light
 * palette's '#FFFFFF' would give roughly 2:1 on these lighter accents — white on
 * #64B5F6 is 2.21:1 — so contained buttons would fail badly while looking fine
 * to anyone not measuring.
 */
const darkPalette = {
  mode: 'dark' as const,
  primary: { main: '#64B5F6', light: '#90CAF9', dark: '#42A5F5', contrastText: '#000000' },
  secondary: { main: '#CE93D8', light: '#E1BEE7', dark: '#BA68C8', contrastText: '#000000' },
  error: { main: '#EF9A9A', light: '#FFCDD2', dark: '#E57373', contrastText: '#000000' },
  warning: { main: '#FFB74D', light: '#FFCC80', dark: '#FFA726', contrastText: '#000000' },
  success: { main: '#81C784', light: '#A5D6A7', dark: '#66BB6A', contrastText: '#000000' },
  info: { main: '#4FC3F7', light: '#81D4FA', dark: '#29B6F6', contrastText: '#000000' },
  text: { primary: '#FFFFFF', secondary: '#C9C9C9', disabled: '#8F8F8F' },
  background: { default: '#121212', paper: '#1E1E1E' },
  divider: '#6E6E6E',
};

const baseThemeOptions: ThemeOptions = {
  palette,
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    // Base font size for accessibility (16px minimum)
    fontSize: 16,
    htmlFontSize: 16,
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Ensure focus indicators are visible
        '*:focus-visible': {
          outline: '3px solid #1565C0',
          outlineOffset: '2px',
        },
        // Skip link styling
        '.skip-link': {
          position: 'absolute',
          left: '-9999px',
          zIndex: 999,
          padding: '1em',
          backgroundColor: '#1565C0',
          color: '#FFFFFF',
          textDecoration: 'none',
          '&:focus': {
            left: '50%',
            transform: 'translateX(-50%)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          // Minimum touch target size (44x44 per WCAG)
          minHeight: 44,
          minWidth: 44,
          textTransform: 'none', // Better readability
          fontWeight: 600,
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // Minimum touch target size
          minHeight: 44,
          minWidth: 44,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'underline', // Always underline links for visibility
          '&:hover': {
            textDecoration: 'none',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
        // Always render the label in its shrunken/floating position so it
        // never overlaps placeholder text or pre-filled values. Anything
        // else creates the "guess what's missing" anti-pattern documented
        // in CLAUDE.md → Form UX Patterns.
        slotProps: { inputLabel: { shrink: true } },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        // Labels are forced to shrink:true above, so the outline must always
        // cut its notch to match. Without this the resting (unfocused) border
        // draws straight through the floating label — it only opened the notch
        // on focus — which read as the label being struck through on Select
        // controls (Findings, Tracked Places). See REV-395.
        notched: true,
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          fontSize: '0.875rem',
        },
      },
    },
    MuiAlert: {
      defaultProps: {
        // REV-561: MUI sets BOTH aria-label and an identical `title` on the
        // dismiss button from its single `closeText` prop. A title that merely
        // repeats the accessible name adds no information and produces a
        // redundant tooltip (engine rule ARIA-32). Drop the title app-wide and
        // keep aria-label, which is the part that names the control.
        slotProps: { closeButton: { title: undefined } },
      },
      styleOverrides: {
        root: {
          // Ensure alert icons have proper aria-hidden
          '& .MuiAlert-icon': {
            marginRight: '12px',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        // REV-482: MUI's default cell border is the divider token (#E0E0E0),
        // only ~1.3:1 on white — fails WCAG 2.2 non-text contrast (1.4.11).
        // Raise row separators to a >=3:1 grey (dogfood requirement).
        root: {
          borderBottomColor: '#8C8C8C',
        },
        // REV-589: resolved from the ACTIVE theme, not from the module-level
        // `palette` constant. That constant is the light palette, and both
        // modes share this one options object (see buildTheme below) — so a
        // literal pinned the header to #FAFAFA in dark mode while the text
        // turned white, giving 1.04:1. Same regression class as the charts in
        // REV-491. A function override is evaluated per mode.
        head: ({ theme }: { theme: Theme }) => ({
          fontWeight: 700,
          backgroundColor: theme.palette.background.default,
        }),
      },
    },
  },
  // Shape configuration
  shape: {
    borderRadius: 8,
  },
  // Spacing based on 8px grid
  spacing: 8,
};

/**
 * REV-491: build the theme for a palette mode.
 *
 * Both modes share ONE options object, so every component override and contrast
 * decision already made — the #8C8C8C table border from REV-482, the Alert
 * close-button title removal from REV-561 — applies in both. Only the palette
 * differs. Building dark by spreading an already-constructed theme would feed
 * MUI its own computed output and quietly drop some of that.
 */
export function createAppTheme(mode: 'light' | 'dark', direction: 'ltr' | 'rtl' = 'ltr') {
  return responsiveFontSizes(
    createTheme({
      ...baseThemeOptions,
      // REV-796: groundwork for a future RTL locale; none ships yet.
      direction,
      palette: mode === 'dark' ? darkPalette : palette,
    })
  );
}

/** Light theme, kept as a named export so existing imports and tests still work. */
export const theme = createAppTheme('light');
